import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { translations } from "../utils/Translation/staticTranslation";
import {
  AdminRolesApi,
  buildSuperAdminRoleCreateBody,
  buildSuperadminRoleNameLookup,
  mapMarketRoleUserToRow,
  mapSuperAdminRoleToRow,
  parsePaginatedListResponse,
  parseSuperadminRoleModulesResponse,
} from "../Apis/admin-roles";
import {
  getCreateRolesApiErrorMessage,
  findRoleSelectValueForMarketKey,
  isPendingSuperadminInvite,
  CREATE_ROLES_PAGE_SIZE,
  buildCreateRolesTablePagination,
} from "../utils/createRolesHelpers";

export function useCreateRolesPage() {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.createRolesPage || {};

  const tabs = useMemo(() => {
    const tr = translations[lang]?.createRolesPage || {};
    return [
      { id: "users", label: tr.tab_users },
      { id: "roles", label: tr.tab_roles },
    ];
  }, [lang]);

  const [activeTab, setActiveTab] = useState("users");
  const [roles, setRoles] = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [modalPermissionRows, setModalPermissionRows] = useState(null);
  const [modalModuleAccessTypes, setModalModuleAccessTypes] = useState(null);
  const [modalModulesLoading, setModalModulesLoading] = useState(false);
  const [modalModulesLoadError, setModalModulesLoadError] = useState(false);
  const [moduleCatalog, setModuleCatalog] = useState(null);
  const [moduleAccessTypesCatalog, setModuleAccessTypesCatalog] =
    useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [rolesPagination, setRolesPagination] = useState({
    current: 1,
    pageSize: CREATE_ROLES_PAGE_SIZE,
    total: 0,
  });
  const [usersPagination, setUsersPagination] = useState({
    current: 1,
    pageSize: CREATE_ROLES_PAGE_SIZE,
    total: 0,
  });

  const marketRoleNameByKey = useMemo(
    () => buildSuperadminRoleNameLookup(roles),
    [roles],
  );

  const fetchRolesList = useCallback(
    async (page = 1) => {
      const tr = translations[lang]?.createRolesPage || {};
      setRolesLoading(true);
      try {
        const res = await AdminRolesApi.listSuperAdminRoles(page);
        const { results, count } = parsePaginatedListResponse(res?.data);
        const mapped = results.map((r) => mapSuperAdminRoleToRow(r, moduleCatalog));
        setRoles(mapped);
        setRolesPagination((prev) => ({
          ...prev,
          total: count,
          current: page,
        }));
      } catch (e) {
        message.error(getCreateRolesApiErrorMessage(e, tr.error_roles_load_failed));
        setRoles([]);
        setRolesPagination((prev) => ({ ...prev, total: 0, current: 1 }));
      } finally {
        setRolesLoading(false);
      }
    },
    [lang, moduleCatalog],
  );

  useEffect(() => {
    fetchRolesList();
  }, [fetchRolesList]);

  const fetchMarketRoleUsersList = useCallback(
    async (page = 1) => {
      const tr = translations[lang]?.createRolesPage || {};
      setUsersLoading(true);
      try {
        const res = await AdminRolesApi.listUsersWithMarketRole(page);
        const { results, count } = parsePaginatedListResponse(res?.data);
        const rows = results
          .map((u) => mapMarketRoleUserToRow(u))
          .filter(Boolean);
        setAssignUsers(rows);
        setUsersPagination((prev) => ({
          ...prev,
          current: page,
          total: count,
        }));
      } catch (e) {
        message.error(getCreateRolesApiErrorMessage(e, tr.error_users_load_failed));
        setAssignUsers([]);
        setUsersPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setUsersLoading(false);
      }
    },
    [lang],
  );

  useEffect(() => {
    if (activeTab !== "users") return;
    fetchMarketRoleUsersList(1);
  }, [activeTab, fetchMarketRoleUsersList]);

  const handleRolesPageChange = useCallback(
    (page) => {
      fetchRolesList(page);
    },
    [fetchRolesList],
  );

  const handleUsersPageChange = useCallback(
    (page) => {
      fetchMarketRoleUsersList(page);
    },
    [fetchMarketRoleUsersList],
  );

  const rolesTablePagination = useMemo(
    () =>
      buildCreateRolesTablePagination({
        current: rolesPagination.current,
        total: rolesPagination.total,
        pageSize: rolesPagination.pageSize,
        onChange: handleRolesPageChange,
      }),
    [rolesPagination, handleRolesPageChange],
  );

  const usersTablePagination = useMemo(
    () =>
      buildCreateRolesTablePagination({
        current: usersPagination.current,
        total: usersPagination.total,
        pageSize: usersPagination.pageSize,
        onChange: handleUsersPageChange,
      }),
    [usersPagination, handleUsersPageChange],
  );

  useEffect(() => {
    if (!roleModalOpen) return undefined;
    let cancelled = false;
    setModalModulesLoading(true);
    setModalModulesLoadError(false);
    setModalPermissionRows(null);
    setModalModuleAccessTypes(null);
    (async () => {
      try {
        const res = await AdminRolesApi.listSuperadminRoleModules();
        const { moduleRows, moduleAccessTypes } =
          parseSuperadminRoleModulesResponse(res?.data, lang);
        if (cancelled) return;
        setModalPermissionRows(moduleRows);
        setModalModuleAccessTypes(moduleAccessTypes);
        if (moduleRows.length) {
          setModuleCatalog(moduleRows);
          setModuleAccessTypesCatalog(moduleAccessTypes);
        }
      } catch {
        if (cancelled) return;
        setModalModulesLoadError(true);
        setModalPermissionRows([]);
        setModalModuleAccessTypes([]);
      } finally {
        if (!cancelled) setModalModulesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roleModalOpen, lang]);

  const roleSelectOptions = useMemo(
    () => roles.map((r) => ({ value: r.id, label: r.name })),
    [roles],
  );

  const userModalInitialRoleId = useMemo(
    () =>
      editingUser
        ? findRoleSelectValueForMarketKey(roles, editingUser.marketRoleKey)
        : undefined,
    [editingUser, roles],
  );

  const closeRoleModal = useCallback(() => {
    setRoleModalOpen(false);
    setEditingRole(null);
  }, []);

  const handleSaveRole = useCallback(
    async (payload) => {
      const tr = translations[lang]?.createRolesPage || {};
      if (payload.id) {
        const hasPermission = Object.values(payload.accessByModule || {}).some(
          (v) => v === "partial" || v === "edit" || v === "full",
        );
        if (!hasPermission) {
          message.error(tr.validation_permissions_required);
          throw new Error("VALIDATION");
        }
        if (!modalPermissionRows?.length) {
          message.error(tr.error_modules_load_failed);
          throw new Error("VALIDATION");
        }
        const body = buildSuperAdminRoleCreateBody({
          roleName: payload.name,
          description: payload.description ?? "",
          accessByModule: payload.accessByModule,
          moduleRows: modalPermissionRows,
        });
        if (!body.module_permissions?.length) {
          message.error(tr.validation_permissions_required);
          throw new Error("VALIDATION");
        }
        const roleIdNum = Number(payload.id);
        if (!Number.isFinite(roleIdNum)) {
          message.error(tr.error_role_update_failed);
          throw new Error("VALIDATION");
        }
        try {
          await AdminRolesApi.updateSuperAdminRole(roleIdNum, body);
          await fetchRolesList(rolesPagination.current);
          message.success(tr.success_role_updated);
        } catch (e) {
          if (e?.message === "VALIDATION") throw e;
          message.error(
            getCreateRolesApiErrorMessage(e, tr.error_role_update_failed),
          );
          throw e;
        }
        return;
      }

      const hasPermission = Object.values(payload.accessByModule || {}).some(
        (v) => v === "partial" || v === "edit" || v === "full",
      );
      if (!hasPermission) {
        message.error(tr.validation_permissions_required);
        throw new Error("VALIDATION");
      }

      if (!modalPermissionRows?.length) {
        message.error(tr.error_modules_load_failed);
        throw new Error("VALIDATION");
      }

      const body = buildSuperAdminRoleCreateBody({
        roleName: payload.name,
        description: payload.description ?? "",
        accessByModule: payload.accessByModule,
        moduleRows: modalPermissionRows,
      });
      if (!body.module_permissions?.length) {
        message.error(tr.validation_permissions_required);
        throw new Error("VALIDATION");
      }

      try {
        const res = await AdminRolesApi.createSuperAdminRole(body);
        const data = res?.data;
        if (data?.id == null) {
          message.error(tr.error_role_create_failed);
          throw new Error("INVALID_RESPONSE");
        }
        await fetchRolesList();
        message.success(tr.success_role_created);
      } catch (e) {
        if (e?.message === "VALIDATION" || e?.message === "INVALID_RESPONSE") {
          throw e;
        }
        message.error(
          getCreateRolesApiErrorMessage(e, tr.error_role_create_failed),
        );
        throw e;
      }
    },
    [lang, modalPermissionRows, fetchRolesList, rolesPagination.current],
  );

  const handleSaveUser = useCallback(
    async (payload) => {
      const tr = translations[lang]?.createRolesPage || {};
      const roleIdNum = Number(payload.roleId);
      if (!Number.isFinite(roleIdNum)) {
        message.error(tr.validation_role_required);
        throw new Error("VALIDATION");
      }
      const email = String(payload.email || "").trim();
      const isEdit = payload.userId != null && String(payload.userId).trim() !== "";
      try {
        if (isEdit) {
          const userIdNum = Number(payload.userId);
          if (!Number.isFinite(userIdNum)) {
            message.error(tr.error_user_update_failed);
            throw new Error("VALIDATION");
          }
          await AdminRolesApi.patchSuperadminUserRole({
            user_id: userIdNum,
            email,
            role_id: roleIdNum,
          });
          await fetchMarketRoleUsersList(usersPagination.current);
          message.success(tr.success_user_updated);
        } else {
          await AdminRolesApi.assignSuperadminUserRole({
            email,
            role_id: roleIdNum,
          });
          await fetchMarketRoleUsersList(usersPagination.current);
          message.success(tr.success_user_created);
        }
      } catch (e) {
        if (e?.message === "VALIDATION") throw e;
        message.error(
          getCreateRolesApiErrorMessage(
            e,
            isEdit ? tr.error_user_update_failed : tr.error_user_create_failed,
          ),
        );
        throw e;
      }
    },
    [lang, fetchMarketRoleUsersList, usersPagination.current],
  );

  const openUserModalForCreate = useCallback(() => {
    setEditingUser(null);
    setUserModalOpen(true);
  }, []);

  const openUserModalForEdit = useCallback((record) => {
    setEditingUser(record);
    setUserModalOpen(true);
  }, []);

  const closeUserModal = useCallback(() => {
    setUserModalOpen(false);
    setEditingUser(null);
  }, []);

  const handleRevokeUser = useCallback(
    (record) => {
      const tr = translations[lang]?.createRolesPage || {};
      const canRevoke =
        record?.apiUserId != null &&
        Number.isFinite(Number(record.apiUserId));
      if (!canRevoke && !isPendingSuperadminInvite(record)) {
        message.error(tr.error_user_revoke_failed);
        return;
      }
      setRevokeTarget(record);
    },
    [lang],
  );

  const closeRevokeModal = useCallback(() => {
    if (revokingUserId) return;
    setRevokeTarget(null);
  }, [revokingUserId]);

  const confirmRevokeUser = useCallback(async () => {
    const record = revokeTarget;
    if (!record) return;
    const tr = translations[lang]?.createRolesPage || {};
    const rowKey = record?.id != null ? String(record.id) : "";
    const cancelInvite = isPendingSuperadminInvite(record);

    if (cancelInvite) {
      const inviteId = Number(record.inviteId);
      if (!Number.isFinite(inviteId)) {
        message.error(tr.error_invite_cancel_failed);
        setRevokeTarget(null);
        return;
      }
      setRevokingUserId(rowKey);
      try {
        await AdminRolesApi.revokeSuperadminUserRole({ invite_id: inviteId });
        await fetchMarketRoleUsersList(usersPagination.current);
        message.success(tr.success_invite_cancelled);
        setRevokeTarget(null);
      } catch (e) {
        message.error(
          getCreateRolesApiErrorMessage(e, tr.error_invite_cancel_failed),
        );
      } finally {
        setRevokingUserId(null);
      }
      return;
    }

    const idNum =
      record?.apiUserId != null ? Number(record.apiUserId) : Number.NaN;
    if (!Number.isFinite(idNum)) {
      message.error(tr.error_user_revoke_failed);
      setRevokeTarget(null);
      return;
    }
    const email = record?.email != null ? String(record.email).trim() : "";

    setRevokingUserId(rowKey);
    try {
      const body = email ? { user_id: idNum, email } : { user_id: idNum };
      await AdminRolesApi.revokeSuperadminUserRole(body);
      await fetchMarketRoleUsersList(usersPagination.current);
      message.success(tr.success_user_revoked);
      setRevokeTarget(null);
    } catch (e) {
      message.error(
        getCreateRolesApiErrorMessage(e, tr.error_user_revoke_failed),
      );
    } finally {
      setRevokingUserId(null);
    }
  }, [revokeTarget, lang, fetchMarketRoleUsersList, usersPagination.current]);

  const revokeModalMode = useMemo(() => {
    if (!revokeTarget) return "revoke";
    return isPendingSuperadminInvite(revokeTarget) ? "cancel_invite" : "revoke";
  }, [revokeTarget]);

  const openCreateRole = useCallback(() => {
    setEditingRole(null);
    setRoleModalOpen(true);
  }, []);

  const openEditRole = useCallback((record) => {
    setEditingRole(record);
    setRoleModalOpen(true);
  }, []);

  return {
    lang,
    t,
    tabs,
    activeTab,
    setActiveTab,
    roles,
    assignUsers,
    roleModalOpen,
    closeRoleModal,
    openCreateRole,
    openEditRole,
    editingRole,
    modalPermissionRows,
    modalModuleAccessTypes,
    modalModulesLoading,
    modalModulesLoadError,
    userModalOpen,
    editingUser,
    openUserModalForCreate,
    openUserModalForEdit,
    closeUserModal,
    userModalInitialRoleId,
    rolesLoading,
    usersLoading,
    handleSaveRole,
    handleSaveUser,
    handleRevokeUser,
    revokingUserId,
    revokeTarget,
    revokeModalMode,
    closeRevokeModal,
    confirmRevokeUser,
    roleSelectOptions,
    moduleCatalog,
    moduleAccessTypesCatalog,
    marketRoleNameByKey,
    rolesTablePagination,
    usersTablePagination,
  };
}
